// App
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

// Types
import type { SimilarListingDetails } from 'app/types/similarListingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers';

// Lodash
import map from 'lodash/map';
import compact from 'lodash/compact';

// Actions
import ContactActions from 'app/shared/flux/actions/ContactActions';
import { logError } from '@zg-rentals/log-error';

// Components
import ContactSimilarListingsCheckboxWrapper from 'app/shared/modules/hdp/form/Contact/ContactSimilarListingsV2/ContactSimilarListingsCheckboxWrapper';
import Button from 'app/shared/core/Button';
import CarouselV3 from 'app/shared/modules/CarouselV3';

// Misc / utils
import { getSimilarListingType, getTrackingFunction } from 'app/shared/utils/clickstreamUtils';
import {
  emitTrackSimilarListingMultiContactMessageButtonClickEvent,
  emitTrackSimilarListingMultiContactSubmitEvent,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import * as S from 'app/shared/modules/hdp/form/Contact/ContactSimilarListingsV2/styles';
import { buildContactParams, isValidSimilarListing } from './contactHelpers';

// Experiments
import type { ListingDetails } from 'app/types/listingDetails.type';

const ContactSimilarListingsV2 = ({
  listing,
  similarListings,
}: {
  listing: ListingDetails;
  similarListings: Array<SimilarListingDetails>;
}) => {
  // Redux
  const dispatch = useDispatch();
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const cachedInquiry = useSelector((state: RootReduxState) => state.user.inquiryDataCache);
  const userToken = useSelector((state: RootReduxState) => state.user.userToken);
  const shownSimilarListings = similarListings;
  const initState: Array<SimilarListingDetails | null> = shownSimilarListings.slice();
  const [listingsToContact, setListingsToContact] = useState(initState);
  const [haveMessagesBeenSent, setHaveMessagesBeenSent] = useState(false);
  const [dynamicButtonText, setDynamicButtonText] = useState('Send messages');
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(true);

  const selectedCount = compact(listingsToContact).length;

  const handleSelectAllChange = () => {
    if (isSelectAllChecked) {
      setListingsToContact(shownSimilarListings.map(() => null));
      setIsSelectAllChecked(false);
    } else {
      setListingsToContact(shownSimilarListings.slice());
      setIsSelectAllChecked(true);
    }
  };

  const handleCheckboxClick = (listing: SimilarListingDetails, idx: number, action: string) => {
    if (action === 'add') {
      const tempArr = listingsToContact.toSpliced(idx, 1, listing);
      const isEveryListingChecked = tempArr.every((item) => item !== null);
      setListingsToContact(tempArr);
      setIsSelectAllChecked(isEveryListingChecked);
    } else {
      const tempArr = listingsToContact.toSpliced(idx, 1, null);
      setListingsToContact(tempArr);
      if (isSelectAllChecked) {
        setIsSelectAllChecked(false);
      }
    }
  };

  const handleSendMessageButtonClick = async () => {
    const compactListingsToContact = compact(listingsToContact);
    const DEFAULT_INQUIRY_TEXT = "I'm interested in your property.";

    // Clickstream event 7276
    emitTrackSimilarListingMultiContactMessageButtonClickEvent({
      listing: listing,
      triggerLocation: 'home_details',
      triggerObject: 'home_details|post_contact_modal',
    });

    // Set state that messages have been sent
    setHaveMessagesBeenSent(true);

    // Change button text to read messages sent
    setDynamicButtonText('Messages sent');

    // Loop through each similar listing and submit a contact
    // IMPORTANT: Must use similarListing here, NOT the original listing prop
    // otherwise, using origin listing causes contacts to go to original property (or get filtered out)
    for await (const similarListing of compactListingsToContact) {
      try {
        // Safety check: don't contact the original listing (should never happen but prevents duplicates)
        if (!isValidSimilarListing(listing, similarListing)) {
          continue;
        }

        const rentalSubmitId = uuidv4();
        const listingType = getSimilarListingType(similarListing);

        // Build contact params using helper (tested in contactHelpers.test.ts)
        const contactParams = buildContactParams(similarListing, userToken, cachedInquiry, DEFAULT_INQUIRY_TEXT);

        await dispatch(
          ContactActions.submitContact({
            listing: contactParams.listing as unknown as Record<string, unknown>,
            inquiryParams: contactParams.inquiryParams as unknown as {
              email: string;
              phone?: string;
              name?: string;
              textToCache?: string;
              displayName?: string;
              address?: string;
              defaultMessage?: boolean;
            },
            rentalSubmitId,
          }),
        );

        // Clickstream event 5575, 5576, 5577 paid, free, paid-demoted
        // Must track similarListing to attribute lead to correct property
        getTrackingFunction({ type: listingType })?.({
          listing: similarListing,
          triggerLocation: 'home_details',
          triggerObject: 'home_details_component|modal_contact_form',
          triggerSource: 'button_to_send_message',
        });

        // Clickstream event 7283 - tracks each similar listing contact submission
        // Must use similarListing to record correct property in analytics
        emitTrackSimilarListingMultiContactSubmitEvent({
          listing: similarListing,
          rentalSubmitId,
          triggerLocation: 'home_details',
          triggerObject: 'home_details|post_contact_modal',
        });
      } catch (err) {
        logError({
          error: err,
          errorType: 'SimilarListingsContactError',
          context: 'Error submitting contact for similar listings in post contact modal',
        });
      }
    }
  };

  if (shownSimilarListings.length === 0) {
    return null;
  }

  const shouldButtonBeDisabled = haveMessagesBeenSent || compact(listingsToContact).length === 0;

  return (
    <S.Container>
      <S.ScrollableContent>
        <S.ContactCtaArticle>
          <S.ContactTextHeading>Contact more properties</S.ContactTextHeading>
          <S.ContactTextBody>
            {"Let more properties know that you're interested. Select the properties and message them all at once."}
          </S.ContactTextBody>
        </S.ContactCtaArticle>
        <S.SelectAllContainer>
          <S.Checkbox
            type="checkbox"
            id="contact-select-all-checkbox"
            checked={isSelectAllChecked}
            onChange={handleSelectAllChange}
            disabled={haveMessagesBeenSent}
          />
          <S.Label htmlFor="contact-select-all-checkbox">
            Select all |{' '}
            <b>
              {selectedCount} {selectedCount === 1 ? 'property' : 'properties'} selected
            </b>
          </S.Label>
        </S.SelectAllContainer>
        <CarouselV3
          aria-label="Similar properties to contact"
          freeScrollMode={isMobile}
          itemsPerPage={1}
          showPaginationDots={true}
          showScrollbar={true}
          style={{ padding: '0px 16px 16px 16px' }}
        >
          {map(shownSimilarListings, (similarListing, idx) => {
            return (
              <React.Fragment key={`similar-listing-${idx}-${similarListing.aliasEncoded}`}>
                <ContactSimilarListingsCheckboxWrapper
                  listing={similarListing}
                  onClick={handleCheckboxClick}
                  idx={idx}
                  isChecked={listingsToContact[idx] !== null}
                  isDisabled={haveMessagesBeenSent}
                />
                <S.ListingWrapper
                  key={similarListing.aliasEncoded}
                  listing={similarListing}
                  triggerLocation="home_details"
                  triggerObject="home_details_component|post_contact_modal"
                  hideContactButton={true}
                  isCarouselCard={true}
                  wrapperAs="div"
                />
              </React.Fragment>
            );
          })}
        </CarouselV3>
      </S.ScrollableContent>
      <S.Footer>
        <Button btnType="primary" size="lg" onClick={handleSendMessageButtonClick} disabled={shouldButtonBeDisabled}>
          {dynamicButtonText}
        </Button>
      </S.Footer>
    </S.Container>
  );
};

ContactSimilarListingsV2.propTypes = {
  similarListings: PropTypes.array,
};

export default ContactSimilarListingsV2;
